import { useTranslation } from 'react-i18next';
import styles from './ImageUploader.module.css';
import { ChangeEvent, DragEvent, useEffect, useState } from 'react';
import { ReactComponent as AddImage } from '@Assets/svg/add_image.svg';
import { ReactComponent as Trash } from '@Assets/svg/trash.svg';
import Label from '../UI/Label';

export interface ImageSlot {
	file?: File;
	previewUrl: string;
	isExisting?: boolean;
	imageId?: number;
}

interface ImageUploaderProps {
	onFilesChange: (files: ImageSlot[]) => void;
	existingImages?: ImageSlot[]; // Immagini già esistenti (fase di modifica car)
}

const ImageUploader = (props: ImageUploaderProps) => {
	const { t } = useTranslation();
	const [images, setImages] = useState<ImageSlot[]>([]);

	// Fase modifica: carica le immagini esistenti
	useEffect(() => {
		if (props.existingImages && props.existingImages.length > 0) {
			// Deep copy per evitare modifiche dirette al prop
			setImages([...props.existingImages.map((img) => ({ ...img }))]);
		} else {
			// Reset delle immagini se non ci sono immagini esistenti
			setImages([]);
		}
	}, [props.existingImages]);

	// Dopo ogni modifica a images chiamo onChange per le immagini
	useEffect(() => {
		props.onFilesChange(images);
	}, [images]);

	// Aggiunge una nuova immagine
	const handleAddFile = (e: ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files || e.target.files.length === 0) return;
		const file = e.target.files[0];
		// Crea un url temporaneo locale per visualizzare l'img senza caricarla sul server
		const previewUrl = URL.createObjectURL(file);

		setImages((prev) => [...prev, { file, previewUrl, isExisting: false }]);
		e.target.value = ''; // Reset input per consentire lo stesso file in futuro
	};

	// Rimuove un'immagine
	const removeImage = (index: number) => {
		setImages((prev) => prev.filter((_, i) => i !== index));
	};

	// Drop
	const handleDrop = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
			const file = e.dataTransfer.files[0];
			const previewUrl = URL.createObjectURL(file);
			setImages((prev) => [...prev, { file, previewUrl, isExisting: false }]);
			e.dataTransfer.clearData();
		}
	};

	// Drag
	const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
	};

	return (
		<div className={styles.mainSlotsContainer}>
			<Label text={t('carDetails.imageUpload')} className={styles.imageInputLabel} />
			<div className={styles.slotsContainer} onDragOver={handleDragOver} onDrop={handleDrop}>
				{/* Slot vuoto: sempre in prima posizione */}
				<div className={styles.slot}>
					<label htmlFor="file-input" className={styles.emptySlot}>
						<AddImage className={styles.addIcon} />
					</label>
					<input id="file-input" type="file" accept="image/*" className={styles.fileInput} onChange={handleAddFile} />
				</div>

				{/* Slot per immagini già caricate */}
				{images.map((imgSlot, idx) => (
					<div key={`image-${idx}-${imgSlot.imageId || 'new'}`} className={styles.slot}>
						<img src={imgSlot.previewUrl} alt="Preview" className={styles.imagePreview} />

						{/* Icona per rimuovere l'immagine */}
						<div
							className={styles.trashIconContainer}
							onClick={(e) => {
								// Previene che il click sull'icona attivi l'input
								e.stopPropagation();
								removeImage(idx);
							}}
						>
							<Trash className={styles.trashIcon} />
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default ImageUploader;
